import React, { useState, useEffect, useRef } from 'react';

const AnimatedCounter = ({ end, start = 0, duration = 2000, suffix = '', displayEnd, className = '' }) => {
  // Parse the target value once — used both for the initial render (so
  // prerender + hydration ALWAYS show the final value) and as the actual
  // animation target on the client.
  const parsedEnd = typeof end === 'string' ? parseFloat(end.replace(/[^0-9.]/g, '')) : end;

  // Always start at the final value. This means:
  //   • Prerender snapshot shows the real number ("85+", "1", "85,000+")
  //   • Client hydration matches the snapshot → no visible 0-flicker
  //   • Search + AI crawlers reading the raw HTML get the real value
  // Below-the-fold counters can still animate later via the IntersectionObserver
  // effect below (which resets `count` to `start` only the first time the
  // section actually scrolls into view on the client).
  const [count, setCount] = useState(parsedEnd);
  const [isVisible, setIsVisible] = useState(false);
  const [done, setDone] = useState(true);
  const counterRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    // Skip the entire animation lifecycle during static prerender or when
    // the user prefers reduced motion — keep the final value.
    if (typeof window !== 'undefined' && window.__PRERENDER__) return;
    if (typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const node = counterRef.current;
    if (!node) return;

    // If the counter is already in the viewport when the page loads (e.g. Hero
    // stats above the fold), leave the final value in place — no need to
    // count from 0 → 85 the user never asked for it.
    const rect = node.getBoundingClientRect();
    const alreadyVisible =
      rect.top < window.innerHeight && rect.bottom > 0;
    if (alreadyVisible) {
      hasAnimated.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          // Reset to start value and let the animation effect run.
          const startValue = typeof start === 'string'
            ? parseFloat(start.replace(/[^0-9.]/g, ''))
            : start;
          setCount(startValue);
          setDone(false);
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.unobserve(node);
  }, [start]);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const endValue = typeof end === 'string' ? parseFloat(end.replace(/[^0-9.]/g, '')) : end;
    const startValue = typeof start === 'string' ? parseFloat(start.replace(/[^0-9.]/g, '')) : start;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.round(startValue + (endValue - startValue) * easeOutQuart);

      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
        setDone(true);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, start, duration]);

  const formatNumber = (num) => {
    // Add commas for thousands
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  return (
    <div ref={counterRef} className={className}>
      {done && displayEnd ? displayEnd : `${formatNumber(count)}${suffix}`}
    </div>
  );
};

export default AnimatedCounter;
